import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { Observable, Subject } from 'rxjs';
import { Document } from '../../../document-data/document.model';
import { DocumentItem } from '../../../document-data/sub-models/document-item.model';
import { ItemCalculationResult } from '../../../document-data/sub-models/item-calculation-result.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { DocumentItemsListItemBuilder } from './document-items-list-item.builder';
import { IDocumentDataBuilder } from '../../../document-data/document-data-builder.interface';

@Component({
  selector: 'app-document-items-list-item',
  templateUrl: 'document-items-list-item.component.html',
  styleUrls: ['document-items-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentItemsListItemBuilder],
})
export class DocumentItemsListItemComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() documentItem: DocumentItem;
  @Input() documentItemCalculationResult: ItemCalculationResult;
  @Input() document: Document;
  @Input() documentTaxes: List<Tax>;
  @Input() documentWithholdingTax: WithholdingTax;
  @Input() documentDataBuilder: IDocumentDataBuilder;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() companySettings: CompanySettings;
  @Input() editingDocumentItemId: string;
  @Input() enableDiscount: boolean;
  @Input() trackingObject: ITrackingObject;
  @Input() sortableHandle: string;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output()
  onDeleteDocumentItem: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output()
  onSaveDocumentItem: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output()
  onSaveItemToList: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output() onDocumentItemEditMode: EventEmitter<any> = new EventEmitter<any>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _editMode$: Subject<boolean> = new Subject<boolean>();

  editMode$: Observable<boolean>;
  itemTappedAction: ITrackingAction;
  itemDeleteAction: ITrackingAction;
  itemTitle: string;
  itemTotalBreakdown: string;
  reorderIcon: IIcon;

  constructor(
    private _moneyPipe: MoneyPipe,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _translateService: TranslateService,
    private _documentItemsListItemBuilder: DocumentItemsListItemBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.documentSettings ||
        changes.documentItem ||
        changes.documentItemCalculationResult) &&
      this.documentSettings &&
      this.documentItem &&
      this.documentItemCalculationResult
    ) {
      const currencyCode: string = this.documentSettings.currencyCode;
      const locale: string = this.documentSettings.locale;
      const itemRate = this._moneyPipe.transform(
        this.documentItem.unitPrice,
        currencyCode,
        locale
      ) as string;
      const showQuantityRate: boolean = this.documentSettings
        .showQuantityAndRate;
      const hasDiscount: boolean =
        this.documentItemCalculationResult.totalDiscount > 0;

      let itemTotalBreakdownKey: string;
      let translateParams: any;

      if (showQuantityRate && hasDiscount) {
        itemTotalBreakdownKey = 'LABEL.QUANTITY_RATE_DISCOUNT';
      } else if (showQuantityRate) {
        itemTotalBreakdownKey = 'LABEL.QUANTITY_RATE';
      } else if (hasDiscount) {
        itemTotalBreakdownKey = 'LABEL.RATE_DISCOUNT';
      }

      if (itemTotalBreakdownKey) {
        translateParams = { itemRate };

        if (showQuantityRate) {
          translateParams = {
            ...translateParams,
            itemQuantity: this._documentItemsListItemBuilder.buildQuantityRate(
              this.documentItem.quantity,
              this.documentItem.unitType,
              this.documentSettings.locale
            ),
          };
        }

        if (hasDiscount) {
          translateParams = {
            ...translateParams,
            itemDiscount: this._moneyPipe.transform(
              this.documentItemCalculationResult.totalDiscount,
              this.documentSettings.currencyCode,
              this.documentSettings.locale
            ) as string,
          };
        }
      }

      this.itemTotalBreakdown = itemTotalBreakdownKey
        ? this._translateService.instant(itemTotalBreakdownKey, translateParams)
        : itemRate;
    }

    if (
      (changes.documentItem || changes.documentSettings) &&
      this.documentItem &&
      this.documentSettings
    ) {
      const showCode: boolean =
        this.documentSettings.showProductCode && !!this.documentItem.code;
      const showName: boolean =
        this.companySettings.productNameEnabled && !!this.documentItem.name;

      this.itemTitle =
        showCode && showName
          ? this.documentItem.code + ' - ' + this.documentItem.name
          : (showCode && this.documentItem.code) ||
            (showName && this.documentItem.name);
    }

    if (changes.documentItem || changes.editingDocumentItemId) {
      this._editMode$.next(
        this.documentItem
          ? this.documentItem.id === this.editingDocumentItemId
          : false
      );
    }
  }

  ngOnInit() {
    const itemId = this.documentItem.id;
    const itemLocalId = this.documentItem.localId;
    const itemInputType = TrackingInputTypeValuesEnum.item;

    this.reorderIcon = {
      name: 'reorder',
      size: IconSizesEnum.small,
    };
    this.itemTappedAction = this._trackingActionBuilder.buildInputTapped(
      itemId,
      itemLocalId,
      itemInputType,
      this.trackingScreenName
    );
    this.itemDeleteAction = this._trackingActionBuilder.buildButtonTapped(
      'delete_item',
      this.trackingScreenName
    );
    this.editMode$ = this._editMode$.asObservable();
  }

  onSave(documentItem: DocumentItem) {
    this.onClose();
    this.onSaveDocumentItem.emit(documentItem);
  }

  onClose() {
    this._editMode$.next(false);
    this.onDocumentItemEditMode.emit(false);
  }

  onClick() {
    this._editMode$.next(true);

    this.onDocumentItemEditMode.emit({
      editMode: true,
      documentItemId: this.documentItem.id,
    });

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.itemTappedAction
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
